import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './stores/auth.store';
import { refreshSession } from './api/auth.api';
import { Layout } from './components/Layout';
import { Spinner } from './components/Spinner';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Servers } from './pages/Servers';
import { Events } from './pages/Events';
import { Channels } from './pages/Channels';
import { Rules } from './pages/Rules';

export function App() {
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const setBootstrapping = useAuthStore((s) => s.setBootstrapping);
  const setAuth = useAuthStore((s) => s.setAuth);

  useEffect(() => {
    refreshSession()
      .then(({ accessToken, user }) => setAuth(accessToken, user))
      .catch(() => {
        // not logged in — fine, stay on /login
      })
      .finally(() => setBootstrapping(false));
  }, [setAuth, setBootstrapping]);

  if (isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{ duration: 5000 }}
      />
      <Routes>
        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <Login />
            </RedirectIfAuthed>
          }
        />
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/servers" element={<Servers />} />
          <Route path="/events" element={<Events />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/settings" element={<Placeholder name="Settings" />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function Placeholder({ name }: { name: string }) {
  return (
    <div className="p-5 md:p-10 max-w-3xl mx-auto">
      <h1 className="font-bold text-3xl text-ink mb-2">{name}</h1>
      <p className="text-ink-muted">Disponibile in una fase successiva del progetto.</p>
    </div>
  );
}
